import React, { useState, useContext } from "react";
import {
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { useSnackbar } from "notistack";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import { EstimationDialog } from "./EstimationDialog"; // Import the dialog component

export const EstimationItem = ({
  id,
  name,
  packages,
  onClick,
  onDelete,
  onSave,
  onCheck,
}) => {
  const { token } = useAuthContext();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const { darkMode } = useContext(ThemeContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isChecked, setIsChecked] = useState(false); // State for checkbox

  const handleCheckboxChange = () => {
    const newCheckedState = !isChecked;
    setIsChecked(newCheckedState);
    onCheck(id, newCheckedState); // Notify parent about the change
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/template/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await axios.post(
        `${process.env.REACT_APP_API_URL}/templatepackage/bulkdelete`,
        {
          templateid: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
      onDelete(id);
    } catch (err) {
      enqueueSnackbar("Error Deleting Template!", { variant: "error" });
      console.error("Error deleting template:", err);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
  };

  const handleModifyClicked = async (e) => {
    e.stopPropagation();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/templatepackage/templateid/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const response1 = await axios.get(
      `${process.env.REACT_APP_API_URL}/templatepayment/templateid/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setDialogData({
      id,
      name: name,
      packages: response.data,
      payments: response1.data,
    });
    setDialogOpen(true); // Open the dialog for modification
  };

  const handleCopyClicked = async (e) => {
    e.stopPropagation();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/templatepackage/templateid/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const response1 = await axios.get(
      `${process.env.REACT_APP_API_URL}/templatepayment/templateid/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setDialogData({
      name: `Copy of ${name}`,
      packages: response.data,
      payments: response1.data,
    });
    setDialogOpen(true); // Open the dialog for copying
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData(null);
  };

  const handleDialogSave = (data) => {
    onSave(data); // Pass the saved data to the parent component
    setDialogOpen(false);
  };

  const handleDeleteClicked = (e) => {
    e.stopPropagation();
    setOpenConfirmDialog(true);
  };

  return (
    <>
      <Box
        sx={{
          px: { xs: "20px", md: "28px" },
          py: "14px",
          background: getColors.primaryMain,
          borderRadius: "5px",
          cursor: "pointer",
          "&:hover": {
            background: getColors.listItemHoverBg,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
          height: { xs: "120px", sm: "150px" },
        }}
        onClick={handleCheckboxChange} // Toggle checkbox when box is clicked
      >
        <Box
          display={"flex"}
          justifyContent="space-between"
          sx={{ alignItems: "flex-start" }}
        >
          <Typography
            sx={{
              fontSize: { xs: "20px", sm: "22px" },
              color: getColors.buttonText,
              fontWeight: 600,
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Typography>
          <FormControlLabel
            sx={{ margin: 0 }}
            control={
              <Checkbox
                checked={isChecked} // Bind the checkbox state
                onChange={handleCheckboxChange} // Allow manual toggling
                sx={{
                  padding: 0,
                  color: "white",
                  "&.Mui-checked": {
                    color: "white",
                  },
                }}
              />
            }
          />
        </Box>
        <Box display={"flex"}>
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleModifyClicked}
          >
            Modify
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: { xs: 1, sm: "3px", md: 1 },
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleCopyClicked}
          >
            Copy
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: { xs: 1, sm: "3px", md: 1 },
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleDeleteClicked}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Confirm Deletion Dialog */}
      <Dialog open={openConfirmDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this template?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              color: darkMode
                ? getColors.appBarBgLight
                : getColors.appBarBgDark,
              textAlign: "center",
              "&: hover": {
                background: "none",
                color: getColors.primaryMain,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modify/Copy Dialog */}
      {dialogOpen && (
        <EstimationDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSave={handleDialogSave}
          initialData={dialogData}
        />
      )}
    </>
  );
};
